import { useState } from "react";
import { buildUrl, useApi, useDebounced } from "../api";
import type { Counted, Paged, Vorgang } from "../api";
import { Empty, ErrorNote, Loading, Pager, formatDate } from "../components";

const LIMIT = 25;

interface TypRow extends Counted {
  typ: string;
}

export default function Vorgaenge() {
  const [query, setQuery] = useState("");
  const [typ, setTyp] = useState("");
  const [initiator, setInitiator] = useState("");
  const [offset, setOffset] = useState(0);
  const search = useDebounced(query);

  const typen = useApi<TypRow[]>(buildUrl("/api/vorgang-typen"));
  const { data, loading, error, reload } = useApi<Paged<Vorgang>>(
    buildUrl("/api/vorgaenge", {
      q: search || null,
      typ: typ || null,
      initiator: initiator || null,
      limit: LIMIT,
      offset,
    }),
  );

  function change<T>(setter: (v: T) => void) {
    return (value: T) => {
      setter(value);
      setOffset(0);
    };
  }

  return (
    <>
      <div className="page-head">
        <h1>Parlamentarische Vorgänge</h1>
        <p>
          Gesetzentwürfe, Anträge und weitere Vorlagen aus dem Dokumentations- und
          Informationssystem des Bundestages. Wer eine Vorlage einbringt, zeigt seine
          Prioritäten — auch dann, wenn sie keine Mehrheit findet.
        </p>
      </div>

      <div className="filters">
        <input
          type="search"
          placeholder="Suche in Titel und Kurzbeschreibung"
          value={query}
          onChange={(e) => change(setQuery)(e.target.value)}
          aria-label="Vorgänge durchsuchen"
        />
        <select
          value={typ}
          onChange={(e) => change(setTyp)(e.target.value)}
          aria-label="Nach Vorgangstyp filtern"
        >
          <option value="">Alle Typen</option>
          {(typen.data ?? []).map((t) => (
            <option key={t.typ} value={t.typ}>
              {t.typ} ({t.anzahl})
            </option>
          ))}
        </select>
        <select
          value={initiator}
          onChange={(e) => change(setInitiator)(e.target.value)}
          aria-label="Nach Urheber filtern"
        >
          <option value="">Alle Urheber</option>
          <option value="CDU/CSU">CDU/CSU</option>
          <option value="SPD">SPD</option>
          <option value="AfD">AfD</option>
          <option value="GRÜNEN">Grüne</option>
          <option value="LINKE">Linke</option>
          <option value="Bundesregierung">Bundesregierung</option>
        </select>
      </div>

      {error ? (
        <ErrorNote message={error} onRetry={reload} />
      ) : loading && !data ? (
        <Loading rows={8} />
      ) : !data || data.items.length === 0 ? (
        <Empty>Keine Vorgänge gefunden. Filter zurücksetzen oder Suchbegriff ändern.</Empty>
      ) : (
        <>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th style={{ width: "6.5rem" }}>Datum</th>
                  <th>Titel</th>
                  <th style={{ width: "9rem" }}>Stand</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((v) => (
                  <tr key={v.id}>
                    <td style={{ whiteSpace: "nowrap", color: "var(--ink-2)" }}>
                      {formatDate(v.datum)}
                    </td>
                    <td>
                      <div style={{ fontWeight: 500 }}>{v.titel}</div>
                      <div
                        style={{
                          fontSize: "0.82rem",
                          color: "var(--ink-3)",
                          marginTop: "0.15rem",
                        }}
                      >
                        {v.typ}
                        {v.initiative && v.initiative.length > 0
                          ? ` · ${v.initiative.join(", ")}`
                          : ""}
                        {" · "}
                        <span className="dip-id">DIP {v.dip_id}</span>
                      </div>
                    </td>
                    <td style={{ fontSize: "0.85rem" }}>{v.beratungsstand ?? "–"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pager offset={offset} limit={LIMIT} total={data.total} onChange={setOffset} />
        </>
      )}
    </>
  );
}
